import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Sanctuary / Small Facility Plan',
      code: 'professional',
      description: 'Perfect for sanctuaries, small zoos, and specialized facilities',
      monthlyPrice: 79,
      yearlyPrice: 800,
      features: [
        '2 users',
        '1 facility',
        'Up to 300 animals',
        'Animal tracking & records',
        'Permit & compliance management',
        'Veterinary records',
        'Document storage',
        'State legality summaries',
        'Audit-ready exports',
        'Enrichment & diet plans',
        'Inspection management',
      ],
      popular: true,
    },
    {
      name: 'Zoo & Institutional Plan',
      code: 'enterprise',
      description: 'Ideal for larger zoos, aquariums, and multi-site organizations',
      monthlyPrice: 149,
      yearlyPrice: 1500,
      features: [
        'Up to 5 users',
        'Up to 3 facilities',
        'Up to 1,000 animals',
        'Everything in Sanctuary Plan',
        'Interactive Analytics & Real-Time Metrics',
        'Transportation management',
        'Multi-facility support',
        'Animal group management',
        'Medication formulary',
        'Regulations database',
      ],
      popular: false,
    },
  ];

  const calculateSavings = (monthly: number, yearly: number) => {
    const yearlySavings = (monthly * 12) - yearly;
    return Math.round((yearlySavings / (monthly * 12)) * 100);
  };

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-muted/30">
      <div className="mx-auto max-w-6xl px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Simple, transparent pricing for every facility
          </h2>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground px-2">
            Start with a 14-day free trial. Cancel anytime without charge.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              billingCycle === 'yearly' ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Yearly
          </span>
          {billingCycle === 'yearly' && (
            <Badge variant="secondary" className="ml-2">
              Save {calculateSavings(plans[0].monthlyPrice, plans[0].yearlyPrice)}%
            </Badge>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="mx-auto mt-6 grid max-w-md grid-cols-1 gap-6 lg:max-w-none lg:grid-cols-2">
          {plans.map((plan) => {
            const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
            const priceLabel = billingCycle === 'monthly' ? '/month' : '/year';

            return (
              <Card
                key={plan.code}
                className={`flex flex-col justify-between ${
                  plan.popular
                    ? 'border-primary shadow-elegant ring-2 ring-primary/20'
                    : 'border-border'
                }`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    {plan.popular && (
                      <Badge className="bg-primary text-primary-foreground text-xs">
                        Popular
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="mt-1 text-sm">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold tracking-tight text-foreground">
                        ${price}
                      </span>
                      <span className="text-sm font-medium text-muted-foreground">
                        {priceLabel}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Billed ${plan.yearlyPrice} annually
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 pt-0">
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => navigate('/auth?tab=signup')}
                    className="w-full mt-6"
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    Start Free Trial
                  </Button>

                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    14-day free trial • Cancel anytime without charge
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Add-ons Section */}
        <div className="mt-8 text-center">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">
            Need more capacity?
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Flexible add-ons available to scale with your organization
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-primary" />
              <span>Additional users: $5/month per user</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-primary" />
              <span>Additional facilities: $10/month per facility</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-primary" />
              <span>Animal packs: $5/month per 200 animals</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
